'use client'

import dynamic from 'next/dynamic'
import { type FC } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'

import {
  type OfferV11,
  OfferV1OfferTypeEnum,
  type PimProductV11,
  type PimProductV1Flags,
} from '@redteclab/api/clients/bully'

import { useDeviceDetectorContext } from '../../../device-detector'
import { ErrorBoundary } from '../../../error-boundary'
import { ExperimentFreeGiftPromotion } from '../../../experiments/components/ExperimentFreeGiftPromotion/ExperimentFreeGiftPromotion'
import { useIsfreeGiftPromotionsExperimentEnabledAndV1 } from '../../../experiments/components/ExperimentFreeGiftPromotion/useGetFreeGiftPromotionsExperiment'
import { ProductVolumePricingContextProvider } from '../../../product'
import { productAccessorIsProductNotInCatalog } from '../../model/productAccessor/productAccessorIsProductNotInCatalog'
import { productAccessorIsProductOutOfSale } from '../../model/productAccessor/productAccessorIsProductOutOfSale'
import { productAccessorIsProductOutOfStock } from '../../model/productAccessor/productAccessorIsProductOutOfStock'
import { PRODUCT_TEMPLATES } from '../../model/productTemplates'
import { ProductPageNowDialog } from '../../now/components/ProductPageNowDialog/ProductPageNowDialog'
import { ProductBuyBox } from '../ProductBuyBox'
import { ProductBuyBoxSticky } from '../ProductBuyBox/ProductBuyBoxSticky'
import { useHideStickyBannerWhenStickyBuyBoxIsVisible } from '../ProductBuyBox/ProductBuyBoxSticky/useHideStickyBannerWhenStickyBuyBoxIsVisible'
import { ProductOfferBox } from '../ProductBuyBox/ProductSellerBox/ProductOffer/ProductOfferBox'
import { ProductBuyBoxIcons } from '../ProductBuyBoxIcons'
import { ProductCarousel } from '../ProductCarousel'
import {
  ProductPageCarouselZoomableImageProvider,
  ProductPageCarouselZoomableImageZoomedImage,
} from '../ProductCarousel/ProductPageCarouselZoomableImage'
import { ProductHead } from '../ProductHead'
import { useProductPageContext } from '../productPageContext'
import { ProductPrescriptionTypeCollapsible } from '../ProductPrescriptionType'

import { ProductOverviewOutOfStockCrossSellSliderWidget } from './ProductOverviewOutOfStockCrossSellSliderWidget'
import { ProductOverviewPrescriptionBonusInfoBanner } from './ProductOverviewPrescriptionBonusInfoBanner'

const ProductFirstImpressionCrossSellSliderWidget = dynamic(async () =>
  import(
    '../ProductCrossSell/ProductCrossSellFirstImpressionSliderWidget'
  ).then((mod) => mod.ProductCrossSellFirstImpressionSliderWidget),
)

const shouldRenderPrescriptionTypes = (
  currentOffer: OfferV11 | undefined,
  productTemplate: PRODUCT_TEMPLATES,
  flags: PimProductV1Flags | undefined,
): boolean => {
  if (flags?.veterinary.active) {
    return false
  }

  const isOtx = productTemplate === PRODUCT_TEMPLATES.OTX

  if (currentOffer) {
    return isOtx && currentOffer.offerType === OfferV1OfferTypeEnum.Sae
  }

  return isOtx
}

const canProductBeBought = (currentVariant: PimProductV11): boolean => {
  return (
    !productAccessorIsProductNotInCatalog(currentVariant.stock_status) &&
    !productAccessorIsProductOutOfStock(currentVariant.stock_status) &&
    !productAccessorIsProductOutOfSale(currentVariant.stock_status)
  )
}

const ProductOverview: FC<{
  BannerAndCrosssellBuyTogether?: FC
  ProductAvailableCsw?: FC
}> = ({ BannerAndCrosssellBuyTogether, ProductAvailableCsw }) => {
  const {
    currentOffer,
    currentPrescriptionType,
    currentVariant,
    isMasterPage,
    product,
    productTemplate,
  } = useProductPageContext()
  const { isIntersecting, ref } = useIntersectionObserver({
    // Sticky Buy Box should be initially hidden as IntersectionObserver works on client-side only during scrolling down the page
    initialIsIntersecting: true,
  })
  const { isMobile } = useDeviceDetectorContext()

  const bonus =
    currentOffer?.prescriptionPriceMap?.[currentPrescriptionType]?.bonus

  const shouldShowStickyBuyBox =
    !isIntersecting && canProductBeBought(currentVariant)
  const isFreeGiftPromotionsExperimentEnabledAndV1 =
    useIsfreeGiftPromotionsExperimentEnabledAndV1(isMobile)

  useHideStickyBannerWhenStickyBuyBoxIsVisible(shouldShowStickyBuyBox)

  return (
    <div ref={ref}>
      <ProductVolumePricingContextProvider
        currentVariant={currentVariant}
        volumeBasedPrices={currentOffer?.volumeBasedPrices ?? []}
      >
        <ProductPageNowDialog />
        <ProductOverviewOutOfStockCrossSellSliderWidget />
        <ErrorBoundary>
          <ProductFirstImpressionCrossSellSliderWidget />
        </ErrorBoundary>
        <ProductHead />
        <div className="flex flex-col pb-7 tablet:flex-row">
          <div className="relative tablet:w-1/2 tablet:flex-1 tablet:pr-2.5">
            <ProductPageCarouselZoomableImageProvider key={currentVariant.upid}>
              <ProductCarousel
                productName={product.name}
                videos={product.videos}
              />
              {product.images && product.images.length > 0 ? (
                <ProductPageCarouselZoomableImageZoomedImage
                  alt={product.name}
                  className="hidden tablet:block"
                />
              ) : null}
            </ProductPageCarouselZoomableImageProvider>
            <div className="hidden tablet:block">
              <ProductBuyBoxIcons />
            </div>

            {!isMobile && isFreeGiftPromotionsExperimentEnabledAndV1 ? (
              <ExperimentFreeGiftPromotion />
            ) : null}
          </div>
          <div className="tablet:w-1/2 tablet:flex-1 tablet:pl-2.5">
            <ProductBuyBox />

            {shouldShowStickyBuyBox ? (
              <ProductBuyBoxSticky isMobile={isMobile} product={product} />
            ) : null}

            {isMobile && isFreeGiftPromotionsExperimentEnabledAndV1 ? (
              <ExperimentFreeGiftPromotion isMobile />
            ) : null}
            <div className="block tablet:hidden">
              <ProductBuyBoxIcons />
            </div>

            <ProductOfferBox />
            {shouldRenderPrescriptionTypes(
              currentOffer,
              productTemplate,
              product.flags,
            ) &&
              !isMasterPage && <ProductPrescriptionTypeCollapsible />}
            {ProductAvailableCsw ? (
              <div className="flex w-full flex-1 flex-col gap-5">
                <ProductAvailableCsw />
                {BannerAndCrosssellBuyTogether ? (
                  <BannerAndCrosssellBuyTogether />
                ) : null}
              </div>
            ) : null}
            <ProductOverviewOutOfStockCrossSellSliderWidget />
            {bonus && bonus > 0 ? (
              <ProductOverviewPrescriptionBonusInfoBanner bonus={bonus} />
            ) : null}
          </div>
        </div>
      </ProductVolumePricingContextProvider>
    </div>
  )
}

export { ProductOverview }
